import type { GoogleMapProps } from '@react-google-maps/api'
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api'
import React, { useCallback, useContext, useEffect, useState } from 'react'

import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import { DepositBanner } from '@awaze/design-system.components.deposit-banner'
import { FailedMapPlaceholder } from '@awaze/design-system.components.map'
import {
  MicroPropertyCard,
  MicroPropertyCardSkeleton,
} from '@awaze/design-system.compositions.micro-property-card'

import { pxToRems } from '@awaze/design-system.utils.react'

import {
  REPLACE_SEARCH_CRITERIA,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awaze/mfe-common.contexts.search'
import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContext,
  TmsContextType,
} from '@awaze/mfe-common.contexts.tms'

import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import { MapPin } from 'src/components/MapPin'
import { PropertyInfoResponse } from 'src/utils/getPropertyInfo'
import { WishlistButton } from '@awaze/design-system.components.wishlist-button'
import { useWishlist } from '@awaze/mfe-common.contexts.wishlist'

import {
  getBrandConfig,
  getDepositSchemeDisplayAmount,
} from '@awaze/mfe-common.configs.brand'

import { Regions } from '@awaze/mfe-common.types-and-mocks.one-search-client'
import {
  CommonSite,
  ICommonAggregations,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { DEFAULT_MAX_LEVEL_MAP_ZOOM, PRICE_FROM_NIGHTS } from 'src/constants'
import { PERFORMANCE_MARKS } from 'src/constants/performance-marks'
import { dataLayerErrorHandler } from 'src/utils/dataLayerErrorHandler'
import { searchCriteriaComparisonString } from 'src/utils/helpers'
import { StyledDynamicMap, WishlistButtonContainer } from './DynamicMap.styles'
import { DynamicMapMarker } from './DynamicMap.types'
import {
  buildMapImpressionAndClickPayload,
  buildPropertyUrlForMapProperties,
  getCoordinatesFromBounds,
  removePopularDestinationFeatureIds,
  handlePropertyImages,
} from './DynamicMap.utils'
import { useGetActiveDepositScheme } from './hooks/useGetActiveDepositScheme'
import { usePropertyInfo } from './hooks/usePropertyInfo'
import { Assert } from 'src/utils/assert'
import { getDepositPriceFormattingCallback } from '../../containers/SearchResults/helpers'

export interface DynamicMapProps {
  /**
   * The Google Maps API Key
   */
  apiKey: string

  /**
   * Brand config
   */
  brand: Brand

  /**
   * Brand for map pin according to theme
   */
  themeBrand: Brand

  /**
   * The default zoom level on map load
   */
  defaultZoom?: number

  /**
   * The ID the map should render with in the DOM
   */
  mapId?: string

  /**
   * The maximum zoom level allowed
   */
  maxZoom?: number

  /**
   * The minium zoom level allowed
   */
  minZoom?: number

  /**
   * The Map aspect ratio
   */
  aspectRatio?: '4:3' | '16:9'

  /**
   * Map translation strings
   */
  translations: {
    failedMapPlaceholderHeading: string
    failedMapPlaceholderText: string
    searchFormMapAreaText: string
  }

  /**
   * ???
   */
  unloadMapOnUnmount?: boolean

  /**
   * Callback to get the property info for a marker
   */
  getPropertyInfo: (propertyId: string) => Promise<PropertyInfoResponse>

  /**
   * The PDP accommodation segment translated
   */
  pdpUrlSegment: string

  /**
   * should map be full height
   */
  fullHeight?: boolean

  /**
   * should wishlist button be visible on Dynamic map micro property card
   */
  wishlistButtonVisible?: boolean
}

export const getWishlistButtonProps = (
  propertyCode: string | undefined,
  isPropertyInWishlist: (propertyId: string) => boolean,
  toggleWishlistItem: (propertyId: string) => void,
  tms: TmsContextType | undefined,
): { isChecked: boolean; onClickCallback: () => void } =>
  propertyCode
    ? {
        isChecked: isPropertyInWishlist(propertyCode.toString()),
        onClickCallback: () => {
          toggleWishlistItem(propertyCode.toString())
          tms?.updateDataLayer(
            'link',
            isPropertyInWishlist(propertyCode.toString())
              ? 'wishlist_click_remove'
              : 'wishlist_click_add',
          )
        },
      }
    : {
        isChecked: false,
        onClickCallback: () => {},
      }

export const DynamicMap: React.FC<DynamicMapProps> = ({
  apiKey,
  brand,
  themeBrand,
  mapId = 'google-map-script',
  unloadMapOnUnmount = true,
  defaultZoom = 7,
  maxZoom = DEFAULT_MAX_LEVEL_MAP_ZOOM,
  minZoom = 3,
  aspectRatio = '4:3',
  fullHeight = false,
  getPropertyInfo,
  pdpUrlSegment,
  translations,
  wishlistButtonVisible = false,
}) => {
  const tms = useContext(TmsContext)
  const { coordinates, searchCriteria, aggregations, rhsLevel, total } =
    useSearchContextState()

  // TODO: Correct types in MFE-Common and remove this hack - the
  // searchContext will error before it can possbily return this as undefined,
  // so it shouldn't be an optional property. If it *was* there would be a runtime error
  // in MFE-Common so may as well throw an error here instead
  Assert.defined(searchCriteria, 'SearchCriteria must be defined')

  const isDatedSearch = Boolean(searchCriteria?.start)
  const aspectRatioArray: Array<number> = aspectRatio
    .split(':')
    .map((val) => parseInt(val))
  const mapHeight = `${(aspectRatioArray[1] / aspectRatioArray[0]) * 100}%`
  const [map, setMap] = useState<google.maps.Map | null>(null)

  const [markers, setMarkers] = useState<Array<DynamicMapMarker> | undefined>(
    [],
  )

  const [isSetBoundsComplete, setBoundsComplete] = useState(false)
  const [userInteracted, setUserInteracted] = useState<boolean>(false)
  const [isZoomEventLocked, setIsZoomEventLocked] = useState(true)
  const [googleMapHasSetInitialBounds, setGoogleMapHasSetInitialBounds] =
    useState(false)
  const [isInitialLoadFinished, setIsInitialLoadFinished] = useState(false)

  const {
    currencySymbol,
    formatCurrency,
    formatCurrencyToObject,
    formatMessage,
    language,
    country,
  } = useIntlContext()
  const dispatch = useSearchContextDispatch()
  const searchCriteriaWithoutNESW = {
    ...searchCriteria,
    ne: null,
    sw: null,
  }
  const searchCriteriaWithoutNESWString = searchCriteriaComparisonString(
    searchCriteriaWithoutNESW,
  )

  const { isLoaded, loadError } = useJsApiLoader({
    id: mapId,
    googleMapsApiKey: apiKey,
    language: language,
    region: country,
  })

  const deposits = getBrandConfig(brand).deposits

  Assert.defined(deposits, 'Deposits must be defined!')

  useEffect(() => {
    if ((markers?.length ?? 0) > 0 && googleMapHasSetInitialBounds) {
      setBoundsComplete(true)
    }
  }, [markers?.length, googleMapHasSetInitialBounds])

  useEffect(() => {
    if (!isInitialLoadFinished && isSetBoundsComplete) {
      setIsInitialLoadFinished(true)
    }
  }, [isSetBoundsComplete])

  useEffect(() => {
    if (map && isLoaded && (coordinates?.length ?? 0) > 0) {
      const bounds = new window.google.maps.LatLngBounds()

      coordinates?.forEach((coordinate) => bounds.extend(coordinate))

      if (!isSetBoundsComplete) {
        map.fitBounds(bounds)
      }

      setMarkers(coordinates)
    }
  }, [coordinates, isLoaded, map])

  useEffect(() => {
    if (isInitialLoadFinished) {
      setIsZoomEventLocked(false)
    }
  }, [isInitialLoadFinished])

  useEffect(() => {
    new DataLayerFactory().getEvent(DataLayerEventType.MapView).update(
      tms,
      // TODO correct types in MFE-Common - runtime code handles undefined fine
      // so the 'aggregations' property should be ICommonAggregations | undefined
      // ...the runtime code *handles* undefined values fine
      { searchCriteria, aggregations: aggregations as ICommonAggregations },
      dataLayerErrorHandler,
    )
  }, [searchCriteriaWithoutNESWString])

  const {
    activeMarkerId,
    propertyInfo,
    onMarkerClick,
    isPropertyInfoError,
    setActiveMarkerId,
  } = usePropertyInfo({
    brand,
    getPropertyInfo,
    isDatedSearch,
  })

  const { activeDeposit } = useGetActiveDepositScheme({
    brand,
    country,
    deposits,
    propertyInfo,
  })
  const showOfferBanner = activeDeposit && propertyInfo
  const depositDisplayAmount =
    activeDeposit &&
    getDepositSchemeDisplayAmount(
      activeDeposit,
      currencySymbol,
      getDepositPriceFormattingCallback(brand, formatCurrencyToObject),
    )
  const showDepositTerms =
    showOfferBanner && propertyInfo?.cardinality === 'MULTI'

  const ActiveDepositSchemeComponent = showOfferBanner ? (
    <DepositBanner
      highlightedMessage={`${formatMessage({
        id: 'search-results.property-list.reserve-today',
        defaultMessage: 'Reserve today for only',
      })} ${depositDisplayAmount}`}
      termsMessage={
        showDepositTerms
          ? formatMessage({
              id: 'search-results.property-list.reserve-for-selected-properties',
              defaultMessage: 'on selected properties',
            })
          : ''
      }
      highlightedFontSize={100}
    />
  ) : undefined

  useEffect(() => {
    if (!propertyInfo) {
      return
    }

    new DataLayerFactory()
      .getEvent(DataLayerEventType.MapProductImpression)
      .update(
        tms,
        buildMapImpressionAndClickPayload(
          searchCriteria,
          aggregations as ICommonAggregations,
          rhsLevel as Regions,
          total as number,
          propertyInfo,
        ),
        dataLayerErrorHandler,
      )
  }, [propertyInfo])

  const handlePropertyClick = (): void => {
    new DataLayerFactory()
      .getEvent(DataLayerEventType.MapProductClick)
      .update(
        tms,
        buildMapImpressionAndClickPayload(
          searchCriteria,
          aggregations as ICommonAggregations,
          rhsLevel as Regions,
          total as number,
          propertyInfo as CommonSite,
        ),
        dataLayerErrorHandler,
      )
  }

  const containerStyle = {
    width: '100%',
    height: fullHeight ? '100%' : 0,
    paddingTop: mapHeight,
    borderRadius: fullHeight ? 0 : pxToRems(8),
  }

  const onLoad: GoogleMapProps['onLoad'] = useCallback(
    async (googleMapsClass: google.maps.Map) => {
      const bounds = new window.google.maps.LatLngBounds()

      googleMapsClass.fitBounds(bounds)

      setMap(googleMapsClass)
    },
    [],
  )

  const onDragEnd: GoogleMapProps['onDragEnd'] = useCallback(() => {
    setUserInteracted(true)
  }, [])

  const onZoomChanged: GoogleMapProps['onZoomChanged'] = useCallback(() => {
    if (!isZoomEventLocked) {
      setUserInteracted(true)
    }
  }, [isZoomEventLocked])

  const onBoundsChanged: GoogleMapProps['onBoundsChanged'] = useCallback(() => {
    if (!isInitialLoadFinished) {
      setGoogleMapHasSetInitialBounds(true)
    }
  }, [isInitialLoadFinished])

  const onIdle: GoogleMapProps['onIdle'] = useCallback(async () => {
    if (map) {
      const bounds = map?.getBounds()
      const shouldTriggerNewSearch =
        isSetBoundsComplete && bounds && userInteracted

      if (shouldTriggerNewSearch) {
        const coordinates = getCoordinatesFromBounds(bounds)
        const ne = `${coordinates.ne.lat}, ${coordinates.ne.lon}`
        const sw = `${coordinates.sw.lat}, ${coordinates.sw.lon}`

        const fullSearchCriteria = {
          ...searchCriteria,
          ...(ne && { ne }),
          ...(sw && { sw }),
          regionName: translations.searchFormMapAreaText,
        }

        setUserInteracted(false)

        // TODO: GD-234 - Leaving this here for now, but this should be removed once the typing for SearchCriteria no longer inccludes regionId
        delete fullSearchCriteria.regionId
        delete fullSearchCriteria.placesId
        delete fullSearchCriteria.radiusCentre
        delete fullSearchCriteria.radiusKm

        if (
          fullSearchCriteria.features &&
          fullSearchCriteria.features.length > 0
        ) {
          fullSearchCriteria.features = removePopularDestinationFeatureIds(
            fullSearchCriteria.features,
          )
        }

        if (
          fullSearchCriteria.siteFeatures &&
          fullSearchCriteria.siteFeatures.length > 0
        ) {
          fullSearchCriteria.siteFeatures = removePopularDestinationFeatureIds(
            fullSearchCriteria.siteFeatures,
          )
        }

        dispatch({
          type: REPLACE_SEARCH_CRITERIA,
          searchCriteria: fullSearchCriteria,
        })
      }
    }
  }, [searchCriteria, map, isSetBoundsComplete, userInteracted])

  const onUnmount = useCallback(function callback() {
    if (unloadMapOnUnmount) {
      setMap(null)
    }
  }, [])

  const onTilesLoaded: GoogleMapProps['onTilesLoaded'] = useCallback(() => {
    if (map) {
      window?.performance &&
        window.performance.mark(PERFORMANCE_MARKS.MAP_TILES_LOADED)
    }
  }, [map])

  const { isPropertyInWishlist, toggleWishlistItem } = useWishlist()
  const propertyCode = propertyInfo?.code
  const wishlistButtonProps = getWishlistButtonProps(
    propertyCode,
    isPropertyInWishlist,
    toggleWishlistItem,
    tms,
  )

  if (loadError) {
    const errorReason = loadError.message ?? ''
    const errorCode = loadError.name ?? ''

    new DataLayerFactory().getEvent(DataLayerEventType.MapViewFail).update(
      tms,
      // TODO update types in MFE-Common (see above)
      {
        searchCriteria,
        aggregations: aggregations as ICommonAggregations,
        errorReason,
        errorCode,
      },
      dataLayerErrorHandler,
    )

    return (
      <FailedMapPlaceholder
        containerStyle={containerStyle}
        errorLabels={{
          heading: formatMessage({
            id: 'map.error.heading',
            defaultMessage: 'Oops, something went wrong',
          }),
          text: formatMessage({
            id: 'map.error.text',
            defaultMessage: `We couldn't load the map right now. Please try and refresh the page`,
          }),
        }}
      />
    )
  }

  const { url, altText } = handlePropertyImages(propertyInfo)

  return isLoaded ? (
    <StyledDynamicMap>
      <GoogleMap
        mapContainerStyle={containerStyle}
        zoom={defaultZoom}
        onIdle={onIdle}
        onDragEnd={onDragEnd}
        onZoomChanged={onZoomChanged}
        onBoundsChanged={onBoundsChanged}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onTilesLoaded={onTilesLoaded}
        options={{
          minZoom,
          maxZoom,
          mapTypeControl: false,
          streetViewControl: false,
          styles: [
            {
              featureType: 'poi.business',
              stylers: [{ visibility: 'off' }],
            },
          ],
        }}
      >
        {markers &&
          Array.isArray(markers) &&
          markers.map((marker) => {
            const showInfoWindow =
              activeMarkerId && activeMarkerId === marker.id

            return (
              <Marker
                icon={{
                  url: MapPin(themeBrand),
                  scaledSize: new google.maps.Size(27, 35, 'px', 'px'),
                }}
                key={`marker_${marker.id}_${marker.lat}|${marker.lng}`}
                onClick={() => onMarkerClick(marker.id)}
                position={marker}
              >
                {showInfoWindow && (
                  <InfoWindow
                    position={marker}
                    options={{
                      disableAutoPan: true,
                    }}
                    onLoad={(thisWindow) => {
                      thisWindow.setOptions({ disableAutoPan: false })
                    }}
                    onCloseClick={() => setActiveMarkerId(null)}
                  >
                    {propertyInfo ? (
                      <>
                        {wishlistButtonVisible && (
                          <WishlistButtonContainer>
                            <WishlistButton
                              label="Add to Wishlist"
                              variant="elevated"
                              showLabel={false}
                              {...wishlistButtonProps}
                            />
                          </WishlistButtonContainer>
                        )}
                        <MicroPropertyCard
                          displayValues={{
                            imageURL: url,
                            imageAlt: altText,
                            title: propertyInfo.displayName ?? '',
                            grade: Number(propertyInfo.grade ?? 0),

                            // TODO fix types in Design system:
                            // these four properties cascade down to the SpecItem component
                            // that actually will actually behave differently at runtime
                            // if the value is undefined - therefore this
                            // component should accept an undefined value for these props
                            guests: propertyInfo.property?.sleeps as number,
                            bedrooms: propertyInfo.property?.bedrooms as number,
                            bathrooms: propertyInfo.property
                              ?.bathrooms as number,
                            pets: propertyInfo.property?.pets as number,
                            pdpURL: buildPropertyUrlForMapProperties(
                              pdpUrlSegment,
                              propertyInfo,
                              searchCriteria,
                            ),
                            onPropertyLinkClick: () => handlePropertyClick(),
                            ...(propertyInfo.price &&
                              !propertyInfo.priceFrom && {
                                price: formatCurrency(propertyInfo.price),
                              }),
                            ...(propertyInfo.wasPrice && {
                              wasPrice: formatCurrency(propertyInfo.wasPrice),
                            }),
                            ...(propertyInfo.priceFrom && {
                              fromPrice: formatCurrency(propertyInfo.priceFrom),
                            }),
                          }}
                          translations={{
                            moreInfo: formatMessage({
                              id: 'general.more-info',
                              defaultMessage: 'More info',
                            }),
                            from: formatMessage({
                              id: 'general.from',
                              defaultMessage: 'From',
                            }),
                            fromNights: `${isDatedSearch ? searchCriteria.nights : PRICE_FROM_NIGHTS} ${formatMessage(
                              {
                                id: 'search-results.property-list.nights',
                                defaultMessage: `nights`,
                              },
                            )}`,
                            outOf: formatMessage({
                              id: 'site.out-of',
                              defaultMessage: 'Out of',
                            }),
                            stars: formatMessage({
                              id: 'site.stars',
                              defaultMessage: 'Stars',
                            }),
                            propertySpecTranslations: {
                              guests: formatMessage({
                                id: 'site.guests',
                                defaultMessage: 'Guests',
                              }),
                              studio: formatMessage({
                                id: 'site.studio',
                                defaultMessage: 'Studio',
                              }),
                              bedrooms: {
                                singular: formatMessage({
                                  id: 'site.bedrooms.singular',
                                  defaultMessage: 'Bedroom',
                                }),
                                plural: formatMessage({
                                  id: 'site.bedrooms.plural',
                                  defaultMessage: 'Bedrooms',
                                }),
                              },
                              bathrooms: {
                                singular: formatMessage({
                                  id: 'site.bathrooms.singular',
                                  defaultMessage: 'Bathroom',
                                }),
                                plural: formatMessage({
                                  id: 'site.bathrooms.plural',
                                  defaultMessage: 'Bathrooms',
                                }),
                              },
                              pets: {
                                singular: formatMessage({
                                  id: 'site.pets.singular',
                                  defaultMessage: 'Pet',
                                }),
                                plural: formatMessage({
                                  id: 'site.bathrooms.plural',
                                  defaultMessage: 'Pets',
                                }),
                                label: formatMessage({
                                  id: 'site.up-to',
                                  defaultMessage: 'Up to',
                                }),
                                allowed: formatMessage({
                                  id: 'site.pets.allowed',
                                  defaultMessage: 'Allowed',
                                }),
                              },
                            },
                          }}
                          {...(showOfferBanner && {
                            offerBanner: ActiveDepositSchemeComponent,
                          })}
                        />
                      </>
                    ) : isPropertyInfoError ? (
                      <div>
                        {formatMessage({
                          id: 'placeholder-for-error-msg',
                          defaultMessage: 'There has been an error',
                        })}
                      </div>
                    ) : (
                      //loading the state display
                      <MicroPropertyCardSkeleton />
                    )}
                  </InfoWindow>
                )}
              </Marker>
            )
          })}
      </GoogleMap>
    </StyledDynamicMap>
  ) : (
    <></>
  )
}
